import React, { useCallback, useEffect, useState, Component } from 'react';
import { Phone, Mail, Globe, MapPin, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/Button';
/*
 * Shared Dentico business-card artwork.
 * Every approved design property (dimensions, bleed, safe zones, logo lockup,
 * QR size/position, divider, contact + address layout, colours, fonts) lives
 * here so the EN / UA / RU documents stay byte-for-byte identical. The ONLY
 * per-language difference is the front-side subtitle passed in via props.
 *
 * UK standard business card
 * Trim:  85 × 55 mm
 * Bleed: 91 × 61 mm (3 mm each side)
 * Safe:  79 × 49 mm (3 mm inside trim)
 *
 * Dentico palette only:
 *   forest #2F4A3F · ivory #F8F5F0 · stone #EFE8DE · beige #D8C4A8 · charcoal #2B2D2F
 */
const FOREST = '#2F4A3F';
const IVORY = '#F8F5F0';
const BEIGE = '#D8C4A8';
const CHARCOAL = '#2B2D2F';
// QR links DIRECTLY to the clinic's WhatsApp chat (wa.me + E.164 number, no leading +).
const WHATSAPP_URL = 'https://wa.me/447345197820';
// High-res, brand-coloured QR: deep-green modules on white, generous quiet zone.
const QR_SRC = `https://api.qrserver.com/v1/create-qr-code/?size=1000x1000&margin=24&color=2F4A3F&bgcolor=FFFFFF&data=${encodeURIComponent(WHATSAPP_URL)}`;
const SCALE = 6; // 1mm ≈ 6px on screen
const BLEED_W = 91 * SCALE;
const BLEED_H = 61 * SCALE;
const TRIM_INSET = 3 * SCALE; // 3mm bleed → trim edge
const SAFE_INSET = 6 * SCALE; // 3mm bleed + 3mm safe margin
type PrintMode = 'none' | 'A-front' | 'A-back' | 'B-front' | 'B-back';
type Variant = 'A' | 'B';
export function BusinessCardArtwork({
  docName,
  subtitle
}: {
  /** Document title shown above the preview (e.g. "Dentico Business Card — Ukrainian"). */
  docName: string;
  /** Front-side subtitle, one entry per balanced line. */
  subtitle: string[];
}) {
  const [variant, setVariant] = useState<Variant>('A');
  const [printMode, setPrintMode] = useState<PrintMode>('none');
  const [showGuides, setShowGuides] = useState(true);
  const handlePrint = useCallback((mode: PrintMode) => {
    setPrintMode(mode);
  }, []);
  useEffect(() => {
    if (printMode !== 'none') {
      const timeout = setTimeout(() => {
        window.print();
        setPrintMode('none');
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [printMode]);
  const printingFront = printMode.endsWith('front');
  const printingBack = printMode.endsWith('back');
  return <div className="min-h-screen bg-stone-100 py-12 px-4 sm:px-6 lg:px-8 flex flex-col items-center print:bg-white print:p-0 print:m-0" data-id="element-1083">
      <div className="max-w-7xl w-full space-y-10 print:space-y-0" data-id="element-1084">
        {/* Header / controls — screen only */}
        <div className="text-center print:hidden" data-id="element-1085">
          <h1 className="text-3xl font-bold" style={{
          color: FOREST,
          fontFamily: '"Playfair Display", Georgia, serif'
        }} data-id="element-1086">
            {docName}
          </h1>
          <p className="mt-2 text-stone-500" data-id="element-1087">
            Double-sided · 85 × 55 mm · 3 mm bleed (91 × 61 mm) · print-ready
          </p>

          {/* Variant tabs */}
          <div className="mt-6 inline-flex rounded-full border border-stone-300 bg-white p-1" data-id="element-1088">
            {(['A', 'B'] as Variant[]).map(v => <button key={v} onClick={() => setVariant(v)} className="px-5 py-2 text-sm font-semibold rounded-full transition-colors" style={variant === v ? {
            backgroundColor: FOREST,
            color: '#fff'
          } : {
            color: FOREST
          }} data-id="element-1089">
                {v === 'A' ? 'Option A — Clinic' : 'Option B — Team'}
              </button>)}
          </div>

          <div className="mt-5 flex items-center justify-center gap-3" data-id="element-1090">
            <button onClick={() => setShowGuides(!showGuides)} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full border border-stone-300 bg-white text-stone-600 hover:bg-stone-50 transition-colors" data-id="element-1091">
              {showGuides ? <Eye className="w-3.5 h-3.5" data-id="element-1092" /> : <EyeOff className="w-3.5 h-3.5" data-id="element-1093" />}
              {showGuides ? 'Guides On' : 'Guides Off'}
            </button>
            <div className="flex items-center gap-3 text-[10px] text-stone-400" data-id="element-1094">
              <span className="flex items-center gap-1" data-id="element-1095">
                <span className="w-3 h-0 border-t border-dashed border-red-400 inline-block" data-id="element-1096" />{' '}
                Trim
              </span>
              <span className="flex items-center gap-1" data-id="element-1097">
                <span className="w-3 h-0 border-t border-dashed border-blue-400 inline-block" data-id="element-1098" />{' '}
                Safe zone
              </span>
            </div>
          </div>
        </div>

        {/* Cards */}
        <div className="flex flex-col lg:flex-row items-center justify-center gap-10 lg:gap-16 print:block" data-id="element-1099">
          {/* FRONT */}
          <div className="flex flex-col items-center gap-3" style={printingBack ? {
          display: 'none'
        } : undefined} data-id="element-1100">
            <span className="text-xs font-medium text-stone-500 uppercase tracking-wider print:hidden" data-id="element-1101">
              Front
            </span>
            <Card data-id="element-1102">
              <CardFront subtitle={subtitle} data-id="element-1103" />
              {showGuides && <GuidesOverlay data-id="element-1104" />}
            </Card>
          </div>

          {/* BACK */}
          <div className="flex flex-col items-center gap-3" style={printingFront ? {
          display: 'none'
        } : undefined} data-id="element-1105">
            <span className="text-xs font-medium text-stone-500 uppercase tracking-wider print:hidden" data-id="element-1106">
              Back
            </span>
            <Card data-id="element-1107">
              {variant === 'A' ? <CardBackClinic data-id="element-1108" /> : <CardBackTeam data-id="element-1109" />}
              {showGuides && <GuidesOverlay data-id="element-1110" />}
            </Card>
          </div>
        </div>

        {/* Export buttons + specs — screen only */}
        <div className="flex flex-col items-center gap-5 pt-4 print:hidden" data-id="element-1111">
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1112">
            <Button onClick={() => handlePrint(`${variant}-front` as PrintMode)} size="lg" data-id="element-1113">
              Save Front as PDF
            </Button>
            <Button onClick={() => handlePrint(`${variant}-back` as PrintMode)} size="lg" data-id="element-1114">
              Save Back as PDF
            </Button>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl px-6 py-4 max-w-md text-center shadow-sm" data-id="element-1115">
            <p className="text-xs font-semibold text-stone-700 mb-2" data-id="element-1116">
              Print Settings
            </p>
            <div className="text-[11px] text-stone-500 space-y-1 leading-relaxed" data-id="element-1117">
              <p data-id="element-1118">
                In the print dialog select <strong data-id="element-1119">"Save as PDF"</strong>
              </p>
              <p data-id="element-1120">
                Paper size: <strong data-id="element-1121">Custom — 91 × 61 mm</strong>
              </p>
              <p data-id="element-1122">
                Margins: <strong data-id="element-1123">None</strong> · Scale: <strong data-id="element-1124">100%</strong>
              </p>
              <p className="pt-1 text-stone-400" data-id="element-1125">
                Export front & back separately, then upload both.
              </p>
            </div>
          </div>
        </div>
      </div>

      <PrintStyles data-id="element-1126" />
    </div>;
}
/* ── Card shell ─────────────────────────────────────────── */
function Card({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="card-bleed relative rounded-xl shadow-2xl overflow-hidden print:shadow-none print:rounded-none" style={{
    width: BLEED_W,
    height: BLEED_H
  }} data-id="element-1127">
      {children}
    </div>;
}
/* ── Dentico wordmark (DENTICO + CLINIC aligned under TICO) ── */
function Wordmark({
  size = 'lg',
  color = IVORY,
  accent = BEIGE,
  withRule = true
}: {
  size?: 'lg' | 'sm';
  color?: string;
  accent?: string;
  withRule?: boolean;
}) {
  const big = size === 'lg';
  return <div className="inline-flex flex-col leading-none" data-id="element-1128">
      <span style={{
      fontFamily: '"Playfair Display", Georgia, serif',
      color,
      fontSize: big ? 44 : 27,
      letterSpacing: big ? '0.22em' : '0.2em',
      fontWeight: 600
    }} data-id="element-1129">
        DENTICO
      </span>
      {withRule && <span className="block self-stretch my-1.5" style={{
      height: 1,
      backgroundColor: accent,
      opacity: 0.85
    }} data-id="element-1130" />}
      <span className="block text-right" style={{
      color,
      fontSize: big ? 11 : 9.5,
      letterSpacing: big ? '0.5em' : '0.42em',
      textTransform: 'uppercase',
      marginTop: withRule ? 0 : 4
    }} data-id="element-1131">
        Clinic
      </span>
    </div>;
}
/* ── FRONT (shared by both variants) ───────────────────── */
function CardFront({
  subtitle
}: {
  subtitle: string[];
}) {
  const twoLine = subtitle.length > 1;
  return <div className="absolute inset-0" style={{
    backgroundColor: FOREST
  }} data-id="element-1132">
      <div className="absolute flex flex-col items-center justify-center text-center" style={{
      top: SAFE_INSET,
      left: SAFE_INSET,
      right: SAFE_INSET,
      bottom: SAFE_INSET
    }} data-id="element-1133">
        <Wordmark size="lg" color={IVORY} accent={BEIGE} withRule={false} data-id="element-1134" />
        <div className="flex flex-col items-center" style={{
        marginTop: 14,
        gap: 3
      }} data-id="element-1135">
          {subtitle.map((line, i) => <span key={i} style={{
          fontSize: twoLine ? 10 : 12,
          letterSpacing: twoLine ? '0.14em' : '0.16em',
          textTransform: 'uppercase',
          color: BEIGE,
          fontFamily: '"DM Sans", sans-serif',
          fontWeight: 500,
          lineHeight: 1.35
        }} data-id="element-1136">
              {line}
            </span>)}
        </div>
      </div>
    </div>;
}
/* ── Contact row (thin-line deep-green icon + text) ─────── */
function ContactRow({
  icon: Icon,
  children
}: {
  icon: typeof Phone;
  children: React.ReactNode;
}) {
  return <div className="flex items-center" style={{
    gap: 10
  }} data-id="element-1137">
      <Icon className="shrink-0" style={{
      width: 16.5,
      height: 16.5,
      color: FOREST
    }} strokeWidth={1.6} data-id="element-1138" />
      <span style={{
      fontSize: 17,
      fontWeight: 500,
      color: FOREST,
      fontFamily: '"DM Sans", sans-serif',
      whiteSpace: 'nowrap'
    }} data-id="element-1139">
        {children}
      </span>
    </div>;
}
function QrBlock() {
  return <div className="flex flex-col items-center justify-center" data-id="element-1140">
      <div style={{
      width: 104,
      height: 104,
      backgroundColor: '#FFFFFF',
      padding: 4
    }} data-id="element-1141">
        <img src={QR_SRC} alt="Scan to message Dentico Clinic on WhatsApp" className="w-full h-full object-contain" width={1000} height={1000} data-id="element-1142" />
      </div>
      <span className="uppercase" style={{
      fontSize: 9,
      letterSpacing: '0.16em',
      color: FOREST,
      marginTop: 9,
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 600
    }} data-id="element-1143">
        WhatsApp Us
      </span>
    </div>;
}
/* ── BACK — Option A (Clinic) ──────────────────────────── */
function CardBackClinic() {
  return <div className="absolute inset-0" style={{
    backgroundColor: '#FFFFFF'
  }} data-id="element-1144">
      <div className="absolute flex flex-col" style={{
      top: SAFE_INSET,
      left: SAFE_INSET,
      right: SAFE_INSET,
      bottom: SAFE_INSET
    }} data-id="element-1145">
        {/* Logo — nudged lower and left-aligned with the QR block's left edge */}
        <div className="mb-auto" style={{
        marginLeft: 40,
        paddingTop: 10
      }} data-id="element-1146">
          <Wordmark size="sm" color={FOREST} accent={BEIGE} withRule={false} data-id="element-1147" />
        </div>

        {/* QR | divider | contacts — QR left edge aligned under the logo's D */}
        <div className="flex items-center justify-start" style={{
        gap: 24,
        marginLeft: 40
      }} data-id="element-1148">
          <QrBlock data-id="element-1149" />
          <div style={{
          width: 1,
          height: 92,
          alignSelf: 'center',
          backgroundColor: BEIGE
        }} data-id="element-1150" />
          <div className="flex flex-col" style={{
          gap: 15
        }} data-id="element-1151">
            <ContactRow icon={Phone} data-id="element-1152">+44 7345 197820</ContactRow>
            <ContactRow icon={Mail} data-id="element-1153">info@denticoclinic.co.uk</ContactRow>
            <ContactRow icon={Globe} data-id="element-1154">denticoclinic.co.uk</ContactRow>
          </div>
        </div>

        {/* Address — centred footer line */}
        <div className="mt-auto pt-3" data-id="element-1155">
          <div className="flex items-center justify-center" style={{
          gap: 8
        }} data-id="element-1156">
            <MapPin className="shrink-0" style={{
            width: 15.5,
            height: 15.5,
            color: FOREST
          }} strokeWidth={1.6} data-id="element-1157" />
            <span style={{
            fontSize: 16,
            color: FOREST,
            fontFamily: '"DM Sans", sans-serif'
          }} data-id="element-1158">
              360–364 City Road, London EC1V 2PY
            </span>
          </div>
        </div>
      </div>
    </div>;
}
/* ── BACK — Option B (Team) ────────────────────────────── */
function CardBackTeam() {
  return <div className="absolute inset-0" style={{
    backgroundColor: '#FFFFFF'
  }} data-id="element-1159">
      <div className="absolute flex flex-col" style={{
      top: SAFE_INSET,
      left: SAFE_INSET,
      right: SAFE_INSET,
      bottom: SAFE_INSET
    }} data-id="element-1160">
        {/* Top: logo + name/role */}
        <div className="flex items-start justify-between" data-id="element-1161">
          <div data-id="element-1162">
            <span style={{
            display: 'block',
            fontSize: 14,
            fontWeight: 600,
            color: FOREST,
            fontFamily: '"DM Sans", sans-serif'
          }} data-id="element-1163">
              Full Name
            </span>
            <span style={{
            display: 'block',
            fontSize: 10,
            color: CHARCOAL,
            marginTop: 2,
            fontFamily: '"DM Sans", sans-serif'
          }} data-id="element-1164">
              Role / Position
            </span>
            <span style={{
            display: 'block',
            fontSize: 8.5,
            color: '#8C7B63',
            marginTop: 4,
            letterSpacing: '0.04em',
            fontFamily: '"DM Sans", sans-serif'
          }} data-id="element-1165">
              GDC No. —
            </span>
          </div>
          <Wordmark size="sm" color={FOREST} accent={BEIGE} withRule={false} data-id="element-1166" />
        </div>

        {/* Middle: QR | divider | contacts */}
        <div className="flex items-center justify-between my-auto" style={{
        gap: 14
      }} data-id="element-1167">
          <QrBlock data-id="element-1168" />
          <div style={{
          width: 1,
          alignSelf: 'stretch',
          backgroundColor: BEIGE,
          margin: '2px 0'
        }} data-id="element-1169" />
          <div className="flex flex-col" style={{
          gap: 9
        }} data-id="element-1170">
            <ContactRow icon={Phone} data-id="element-1171">+44 7345 197820</ContactRow>
            <ContactRow icon={Mail} data-id="element-1172">info@denticoclinic.co.uk</ContactRow>
            <ContactRow icon={Globe} data-id="element-1173">denticoclinic.co.uk</ContactRow>
          </div>
        </div>

        {/* Address bottom */}
        <div className="pt-2" data-id="element-1174">
          <div className="flex items-center" style={{
          gap: 10
        }} data-id="element-1175">
            <MapPin className="shrink-0" style={{
            width: 13,
            height: 13,
            color: FOREST
          }} strokeWidth={1.6} data-id="element-1176" />
            <span style={{
            fontSize: 10.5,
            color: CHARCOAL,
            fontFamily: '"DM Sans", sans-serif'
          }} data-id="element-1177">
              360–364 City Road, London EC1V 2PY
            </span>
          </div>
        </div>
      </div>
    </div>;
}
/* ── Trim + safe-zone guides (screen only) ─────────────── */
function GuidesOverlay() {
  return <div className="absolute inset-0 pointer-events-none print:hidden" data-id="element-1178">
      <div className="absolute border border-dashed" style={{
      top: TRIM_INSET,
      left: TRIM_INSET,
      right: TRIM_INSET,
      bottom: TRIM_INSET,
      borderColor: 'rgba(239,68,68,0.45)'
    }} data-id="element-1179" />
      <div className="absolute border border-dashed" style={{
      top: SAFE_INSET,
      left: SAFE_INSET,
      right: SAFE_INSET,
      bottom: SAFE_INSET,
      borderColor: 'rgba(59,130,246,0.35)'
    }} data-id="element-1180" />
    </div>;
}
function PrintStyles() {
  return <style data-id="element-1181">{`
      @media print {
        @page { size: 91mm 61mm; margin: 0; }
        * { print-color-adjust: exact !important; -webkit-print-color-adjust: exact !important; }
        html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; overflow: hidden !important; }
        h1, button, nav, header, footer, .print\\:hidden, [class*="print:hidden"] { display: none !important; }
        .min-h-screen { display: block !important; min-height: 0 !important; padding: 0 !important; margin: 0 !important; background: #fff !important; }
        .max-w-7xl { max-width: none !important; padding: 0 !important; margin: 0 !important; }
        .card-bleed {
          position: fixed !important; top: 0 !important; left: 0 !important;
          width: ${BLEED_W}px !important; height: ${BLEED_H}px !important;
          border-radius: 0 !important; border: none !important; box-shadow: none !important;
          overflow: hidden !important; transform-origin: top left !important;
          transform: scale(calc(91mm / ${BLEED_W}px), calc(61mm / ${BLEED_H}px)) !important;
        }
        .card-bleed .pointer-events-none { display: none !important; }
      }
    `}</style>;
}